/* eslint-disable local-rules/require-use-client */
import { Radio } from '@headlessui/react'
import { useEffect, useState } from 'react'

import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

import { SKUSelectorV2Dropdown } from './SKUSelectorV2Dropdown'
import type { SKUSelectorV2RadioOption } from './SKUSelectorV2Props'

export interface SKUSelectorV2RadioProps {
  option: SKUSelectorV2RadioOption
  selectedSku: string
  isLoading: boolean
  previewAllItems: boolean
  onChange: (sku: string) => void
}

export function SKUSelectorV2Radio({
  option,
  selectedSku,
  previewAllItems,
  isLoading,
  onChange,
}: SKUSelectorV2RadioProps) {
  const { dropdownOptions } = option

  // keep track of the last selected dropdown option in this radio option
  const [activeDropdownOption, setActiveDropdownOption] = useState(dropdownOptions?.[0])
  useEffect(() => {
    setActiveDropdownOption(
      (current) => dropdownOptions?.find((option) => option.sku === selectedSku) ?? current
    )
  }, [dropdownOptions, selectedSku])

  const activeOption = option.isDropdown ? activeDropdownOption : option
  const isSelected = selectedSku === activeOption?.sku
  const isPreview = previewAllItems && getBuilderPreviewMode().edit

  return (
    <Radio
      className="rounded-lg outline-bodi-black-text"
      value={activeOption?.sku}
      disabled={isLoading}
    >
      <div
        className={cn([
          'flex shrink-0 flex-col gap-3 rounded-lg p-4 outline outline-1 outline-bodi-gray-butters hover:outline-bodi-black-text lg:p-6',
          isSelected && 'outline-2 outline-bodi-black-text',
        ])}
      >
        <div className="flex w-full shrink-0 gap-3">
          <div
            className={cn(
              'flex size-[22px] items-center justify-center rounded-full bg-bodi-gray-pearl outline outline-2 outline-bodi-gray-butters',
              isSelected && 'bg-transparent outline-bodi-green-deep-500'
            )}
          >
            <div
              className={cn(
                'size-3 rounded-full bg-transparent',
                isSelected && 'bg-bodi-input-selected'
              )}
            ></div>
          </div>
          {option.isDropdown ? (
            <SKUSelectorV2Dropdown
              dropdownOptions={option.dropdownOptions}
              activeOption={activeDropdownOption}
              selectedSku={selectedSku}
              isLoading={isLoading}
              onChange={onChange}
              previewAllItems={isPreview}
            />
          ) : (
            <div className="flex-1">{activeOption?.content}</div>
          )}
        </div>
      </div>
    </Radio>
  )
}
